/**
 * Password Breach Check using HaveIBeenPwned API
 * Uses k-anonymity model - only first 5 chars of SHA-1 hash are sent
 * This ensures the actual password is never transmitted
 */

async function sha1(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

export interface BreachCheckResult {
  isBreached: boolean;
  count: number;
  error?: string;
}

/**
 * Check if a password has been exposed in known data breaches
 * Uses HaveIBeenPwned API with k-anonymity (range query)
 * 
 * @param password - The password to check
 * @returns Object with isBreached boolean and breach count
 */
export async function checkPasswordBreach(password: string): Promise<BreachCheckResult> {
  try {
    // Generate SHA-1 hash of the password
    const hash = await sha1(password);
    
    // Split into prefix (first 5 chars) and suffix (rest)
    const prefix = hash.substring(0, 5);
    const suffix = hash.substring(5);
    
    // Query the HaveIBeenPwned API with just the prefix
    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: {
        'Add-Padding': 'true', // Adds fake responses to prevent response-length analysis
      },
    });
    
    if (!response.ok) {
      // Don't block signup if API is unavailable
      console.warn('Password breach check unavailable:', response.status);
      return { isBreached: false, count: 0 };
    }
    
    const text = await response.text();
    const lines = text.split('\n');
    
    // Search for our suffix in the response
    for (const line of lines) {
      const [hashSuffix, countStr] = line.split(':');
      if (hashSuffix.trim() === suffix) {
        const count = parseInt(countStr.trim(), 10);
        return { isBreached: true, count };
      }
    }
    
    return { isBreached: false, count: 0 };
  } catch (error) {
    // Don't block signup if check fails - just log and continue
    console.warn('Password breach check failed:', error);
    return { isBreached: false, count: 0, error: 'Check unavailable' };
  }
}

/**
 * Format breach count for user-friendly display
 */
export function formatBreachCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}
